import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { settingsService } from '../services/settingsService';

const WebhookContext = createContext();

export const useWebhook = () => {
  const context = useContext(WebhookContext);
  if (!context) {
    throw new Error('useWebhook must be used within a WebhookProvider');
  }
  return context;
};

export const WebhookProvider = ({ children }) => {
  const [webhooks, setWebhooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [webhookLogs, setWebhookLogs] = useState([]);

  // Webhook event types
  const eventTypes = {
    'pipeline.started': 'Pipeline Started',
    'pipeline.completed': 'Pipeline Completed',
    'pipeline.failed': 'Pipeline Failed',
    'pipeline.cancelled': 'Pipeline Cancelled',
    'connection.created': 'Connection Created',
    'connection.updated': 'Connection Updated',
    'connection.deleted': 'Connection Deleted',
    'connection.failed': 'Connection Failed',
    'user.login': 'User Login',
    'user.logout': 'User Logout',
    'user.created': 'User Created',
    'system.error': 'System Error',
    'system.warning': 'System Warning',
    'kafka.topic.created': 'Kafka Topic Created',
    'kafka.message.failed': 'Kafka Message Failed',
    'security.breach': 'Security Breach',
    'security.login.failed': 'Failed Login Attempt'
  };

  // Load webhooks
  useEffect(() => {
    loadWebhooks();
    loadWebhookLogs();
  }, []);

  const loadWebhooks = async () => {
    try {
      const response = await fetch('/api/webhooks');
      if (response.ok) {
        const data = await response.json();
        setWebhooks(data);
      }
    } catch (error) {
      logger.error('Failed to load webhooks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadWebhookLogs = async () => {
    try {
      const response = await fetch('/api/webhooks/logs?limit=100');
      if (response.ok) {
        const data = await response.json();
        setWebhookLogs(data);
      }
    } catch (error) {
      logger.error('Failed to load webhook logs:', error);
    }
  };

  // Create webhook
  const createWebhook = async (webhookData) => {
    try {
      const response = await fetch('/api/webhooks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...webhookData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          last_triggered: null,
          trigger_count: 0,
          failure_count: 0,
          active: true
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create webhook');
      }

      const newWebhook = await response.json();
      setWebhooks(prev => [...prev, newWebhook]);
      return newWebhook;
    } catch (error) {
      logger.error('Failed to create webhook:', error);
      throw error;
    }
  };

  // Update webhook
  const updateWebhook = async (id, updates) => {
    try {
      const response = await fetch(`/api/webhooks/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...updates,
          updated_at: new Date().toISOString()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update webhook');
      }

      const updatedWebhook = await response.json();
      setWebhooks(prev => prev.map(webhook => 
        webhook.id === id ? updatedWebhook : webhook
      ));
      return updatedWebhook;
    } catch (error) {
      logger.error('Failed to update webhook:', error);
      throw error;
    }
  };

  // Delete webhook
  const deleteWebhook = async (id) => {
    try {
      const response = await fetch(`/api/webhooks/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete webhook');
      }

      setWebhooks(prev => prev.filter(webhook => webhook.id !== id));
      return true;
    } catch (error) {
      logger.error('Failed to delete webhook:', error);
      throw error;
    }
  };

  // Test webhook
  const testWebhook = async (id, eventType = 'test') => {
    try {
      const response = await fetch(`/api/webhooks/${id}/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_type: eventType,
          test: true,
          timestamp: new Date().toISOString(),
          data: {
            message: 'This is a test webhook payload',
            source: 'webhook_test',
            test_id: Math.random().toString(36).substr(2, 9)
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Webhook test failed');
      }

      const result = await response.json();
      
      // Reload logs to show the test result
      await loadWebhookLogs();
      
      return result;
    } catch (error) {
      logger.error('Webhook test failed:', error);
      throw error;
    }
  };

  // Toggle webhook active status
  const toggleWebhook = async (id, active) => {
    return updateWebhook(id, { active });
  };

  // Send webhook (used internally by the application)
  const sendWebhook = useCallback(async (eventType, data) => {
    const relevantWebhooks = webhooks.filter(webhook => 
      webhook.active && 
      (webhook.events.includes(eventType) || webhook.events.includes('*'))
    );

    if (relevantWebhooks.length === 0) return;

    const payload = {
      event_type: eventType,
      timestamp: new Date().toISOString(),
      data: data
    };

    // Send to all relevant webhooks
    const promises = relevantWebhooks.map(async (webhook) => {
      try {
        const response = await fetch(webhook.url, {
          method: webhook.method || 'POST',
          headers: {
            'Content-Type': 'application/json',
            'User-Agent': 'DataMolecule-Webhooks/1.0',
            ...webhook.headers
          },
          body: JSON.stringify(payload),
          timeout: webhook.timeout || 10000
        });

        const success = response.ok;
        
        // Log the webhook call
        await logWebhookCall(webhook.id, eventType, payload, success, response.status);
        
        // Update webhook stats
        await updateWebhookStats(webhook.id, success);
        
        return { webhookId: webhook.id, success, status: response.status };
      } catch (error) {
        logger.error(`Webhook ${webhook.id} failed:`, error);
        
        // Log the failure
        await logWebhookCall(webhook.id, eventType, payload, false, 0, error.message);
        await updateWebhookStats(webhook.id, false);
        
        return { webhookId: webhook.id, success: false, error: error.message };
      }
    });

    return Promise.all(promises);
  }, [webhooks]);

  // Log webhook call
  const logWebhookCall = async (webhookId, eventType, payload, success, statusCode, errorMessage = null) => {
    try {
      const logEntry = {
        webhook_id: webhookId,
        event_type: eventType,
        payload: JSON.stringify(payload),
        success,
        status_code: statusCode,
        error_message: errorMessage,
        timestamp: new Date().toISOString()
      };

      await fetch('/api/webhooks/logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logEntry)
      });
    } catch (error) {
      logger.error('Failed to log webhook call:', error);
    }
  };

  // Update webhook statistics
  const updateWebhookStats = async (webhookId, success) => {
    try {
      const webhook = webhooks.find(w => w.id === webhookId);
      if (!webhook) return;

      const updates = {
        last_triggered: new Date().toISOString(),
        trigger_count: (webhook.trigger_count || 0) + 1
      };

      if (!success) {
        updates.failure_count = (webhook.failure_count || 0) + 1;
      }

      await updateWebhook(webhookId, updates);
    } catch (error) {
      logger.error('Failed to update webhook stats:', error);
    }
  };

  // Validate webhook URL
  const validateWebhookUrl = (url) => {
    try {
      const urlObj = new URL(url);
      
      // Must be HTTPS in production
      if (process.env.NODE_ENV === 'production' && urlObj.protocol !== 'https:') {
        return { valid: false, error: 'HTTPS is required for webhook URLs in production' };
      }
      
      // Block internal/private IPs
      const hostname = urlObj.hostname;
      if (hostname === 'localhost' || 
          hostname === '127.0.0.1' || 
          hostname.startsWith('192.168.') ||
          hostname.startsWith('10.') ||
          hostname.startsWith('172.')) {
        return { valid: false, error: 'Cannot use internal/private IP addresses' };
      }
      
      return { valid: true };
    } catch (error) {
      return { valid: false, error: 'Invalid URL format' };
    }
  };

  // Get webhook statistics
  const getWebhookStats = useCallback(() => {
    const stats = {
      total: webhooks.length,
      active: webhooks.filter(w => w.active).length,
      inactive: webhooks.filter(w => !w.active).length,
      totalTriggers: webhooks.reduce((sum, w) => sum + (w.trigger_count || 0), 0),
      totalFailures: webhooks.reduce((sum, w) => sum + (w.failure_count || 0), 0)
    };
    
    stats.successRate = stats.totalTriggers > 0 
      ? ((stats.totalTriggers - stats.totalFailures) / stats.totalTriggers) * 100 
      : 100;
      
    return stats;
  }, [webhooks]);

  // Format webhook log for display
  const formatWebhookLog = (log) => {
    return {
      ...log,
      timeAgo: getTimeAgo(log.timestamp),
      statusText: log.success ? 'Success' : 'Failed',
      statusColor: log.success ? 'text-green-600' : 'text-red-600'
    };
  };

  // Get time ago string
  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Webhook convenience methods for common events
  const notifyPipelineStarted = useCallback((pipelineData) => {
    return sendWebhook('pipeline.started', pipelineData);
  }, [sendWebhook]);

  const notifyPipelineCompleted = useCallback((pipelineData) => {
    return sendWebhook('pipeline.completed', pipelineData);
  }, [sendWebhook]);

  const notifyPipelineFailed = useCallback((pipelineData) => {
    return sendWebhook('pipeline.failed', pipelineData);
  }, [sendWebhook]);

  const notifySecurityBreach = useCallback((securityData) => {
    return sendWebhook('security.breach', securityData);
  }, [sendWebhook]);

  const value = {
    webhooks,
    webhookLogs: webhookLogs.map(formatWebhookLog),
    eventTypes,
    isLoading,
    stats: getWebhookStats(),
    
    // CRUD operations
    createWebhook,
    updateWebhook,
    deleteWebhook,
    toggleWebhook,
    
    // Testing and validation
    testWebhook,
    validateWebhookUrl,
    
    // Event sending
    sendWebhook,
    notifyPipelineStarted,
    notifyPipelineCompleted,
    notifyPipelineFailed,
    notifySecurityBreach,
    
    // Data loading
    loadWebhooks,
    loadWebhookLogs
  };

  return (
    <WebhookContext.Provider value={value}>
      {children}
    </WebhookContext.Provider>
  );
};

export default WebhookContext;